import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import { Terminal } from "../leads/terminals";
import { locationIdForFirestore } from "./locationId";

const convertTimestamp = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (timestamp instanceof Date) return Number.isNaN(timestamp.getTime()) ? null : timestamp;
  if (typeof timestamp === "string") {
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const serializeDate = (d: Date): string =>
  d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : new Date().toISOString();

const trimOptional = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

export const terminalConverter: FirestoreDataConverter<Terminal> = {
  toFirestore(terminal: Terminal) {
    const { id: _id, ...rest } = terminal;
    return {
      pickup_location_id: locationIdForFirestore(rest.pickup_location_id),
      ...(trimOptional(rest.name) ? { name: trimOptional(rest.name) } : {}),
      ...(trimOptional(rest.abbreviation)
        ? { abbreviation: trimOptional(rest.abbreviation) }
        : {}),
      ...(trimOptional(rest.number) ? { number: trimOptional(rest.number) } : {}),
      ...(trimOptional(rest.notes) ? { notes: trimOptional(rest.notes) } : {}),
      createdAt: serializeDate(rest.createdAt),
      updatedAt: serializeDate(rest.updatedAt),
      createdByUserId: rest.createdByUserId ?? "",
    };
  },

  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): Terminal {
    const data = snapshot.data(options) ?? {};
    const pickupLocationId =
      typeof data.pickup_location_id === "string" && data.pickup_location_id.trim()
        ? data.pickup_location_id.trim()
        : null;

    const name = trimOptional(data.name);
    const abbreviation = trimOptional(data.abbreviation);
    const number = trimOptional(data.number);
    const notes = trimOptional(data.notes);

    return {
      id: snapshot.id,
      pickup_location_id: pickupLocationId,
      ...(name ? { name } : {}),
      ...(abbreviation ? { abbreviation } : {}),
      ...(number ? { number } : {}),
      ...(notes ? { notes } : {}),
      createdAt: convertTimestamp(data.createdAt) || new Date(),
      updatedAt: convertTimestamp(data.updatedAt) || new Date(),
      createdByUserId:
        typeof data.createdByUserId === "string" ? data.createdByUserId : "",
    };
  },
};
